<template>
  <!--
    AB#21684 — the Ask Atlas launcher, drawn by the portal.

    Zendesk Messaging cannot put an image on its launcher bubble, so that bubble
    is hidden (useZendeskWidget.takeOverLauncher) and this is what people see:
    the Atlas portrait on the lavender disc, in the exact bottom-right spot the
    Zendesk launcher occupied — so every drawer's ~92px clearance still holds.

    Renders only once the widget has said it is usable; a launcher that opens
    nothing is worse than none. Hidden while the conversation is open, because
    Zendesk's window has its own close control and two Atlases would be odd.
    Unread replies show as a badge (Messaging reports the count).
  -->
  <Transition name="al-pop">
    <button
      v-if="ready && !isOpen"
      type="button"
      class="atlas-launcher"
      :title="label"
      :aria-label="label"
      data-test="atlas-launcher"
      @click="openConversation"
    >
      <AgentAvatar agent="atlas" :size="46" />
      <span v-if="unread > 0" class="atlas-launcher-badge" data-test="atlas-launcher-unread">{{
        unread > 9 ? '9+' : unread
      }}</span>
    </button>
  </Transition>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import AgentAvatar from '@/components/Shared/AgentAvatar.vue';
import { useAtlasLauncher } from '@/composables/useZendeskWidget';

const { ready, isOpen, unread, openConversation } = useAtlasLauncher();

const label = computed(() =>
  unread.value > 0
    ? `Ask Atlas — ${unread.value} unread ${unread.value === 1 ? 'reply' : 'replies'}`
    : 'Ask Atlas'
);
</script>

<style lang="scss" scoped>
/* Same corner and footprint as Zendesk's launcher (64px incl. its margin), so
   the drawers' reserved clearance (AB#21165) is unchanged. Above drawers
   (10036 since AB#21862) and their scrims, below nothing of ours. */
.atlas-launcher {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 10070;
  width: 60px;
  height: 60px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  display: grid;
  place-items: center;
  cursor: pointer;
  background: linear-gradient(140deg, #efe9ff, #e4f7fb);
  box-shadow:
    0 6px 18px rgba(15, 18, 24, 0.28),
    0 0 0 1px rgba(15, 18, 24, 0.06);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
}
.atlas-launcher:hover {
  transform: translateY(-1px);
  box-shadow:
    0 10px 24px rgba(15, 18, 24, 0.32),
    0 0 0 1px rgba(15, 18, 24, 0.06);
}
.atlas-launcher:focus-visible {
  outline: 2px solid var(--accent);
  outline-offset: 3px;
}
.atlas-launcher-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--danger);
  color: #fff;
  font-size: 11px;
  font-weight: 800;
  line-height: 20px;
  text-align: center;
  box-shadow: 0 0 0 2px var(--surface);
}
.al-pop-enter-active,
.al-pop-leave-active {
  transition:
    transform 0.2s cubic-bezier(0.2, 0.9, 0.3, 1.3),
    opacity 0.2s ease;
}
.al-pop-enter-from,
.al-pop-leave-to {
  transform: scale(0.6);
  opacity: 0;
}
/*
  AB#22137 — on a phone the bottom bar owns the bottom edge, and the launcher
  in its corner sat on top of "More". It floats just above the bar instead,
  a little smaller. Over a sheet or the full-screen order (where the bar is
  covered) it goes back to the corner: see assets/phone.scss.
*/
@media (max-width: 767.98px) {
  .atlas-launcher {
    right: 12px;
    bottom: calc(60px + env(safe-area-inset-bottom, 0px) + 12px);
    width: 52px;
    height: 52px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .atlas-launcher,
  .al-pop-enter-active,
  .al-pop-leave-active {
    transition: none;
  }
}
</style>
